// src/pages/Reports/Reports.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: { fontFamily: '"Inter", sans-serif' },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap'
    },
    title: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#212529'
    },
    dateFilterContainer: {
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        background: 'white',
        padding: '10px 15px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
    },
    dateInput: { 
        padding: '10px', 
        fontSize: '16px', 
        border: '1px solid #dee2e6', 
        borderRadius: '6px' 
    },
    grid: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
        gap: '20px' 
    },
    card: { 
        background: 'white', 
        padding: '30px', 
        borderRadius: '8px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)' 
    },
    cardTitle: { 
        fontSize: '16px', 
        fontWeight: '500', 
        color: '#6c757d', 
        marginBottom: '10px' 
    },
    cardValue: { 
        fontSize: '32px', 
        fontWeight: 'bold', 
        color: '#212529' 
    },
    income: { color: '#198754' },
    expense: { color: '#dc3545' },
    balance: { color: '#06C167' },
    table: { 
        width: '100%', 
        borderCollapse: 'collapse', 
        marginTop: '15px' 
    },
    th: { 
        background: '#f8f9fa', 
        padding: '12px 15px', 
        textAlign: 'left', 
        borderBottom: '2px solid #e9ecef',
        color: '#6c757d',
        fontSize: '14px',
        fontWeight: '600'
    },
    td: { 
        padding: '15px', 
        borderBottom: '1px solid #e9ecef',
        color: '#495057'
    },
    errorText: { color: 'red', textAlign: 'center', padding: '20px', background: '#fff0f0', borderRadius: '8px' }
};

function Reports() {
    const { userData } = useAuth();
    const [salesInvoices, setSalesInvoices] = useState([]);
    const [purchaseInvoices, setPurchaseInvoices] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;
        const productsQuery = query(collection(db, 'companies', companyId, 'products'));
        const unsubProducts = onSnapshot(productsQuery, 
            (snapshot) => {
                setProducts(snapshot.docs.map(d => ({id: d.id, ...d.data()})));
            },
            (err) => {
                console.error("Ürünler yüklenirken hata:", err);
                setError("Ürün listesi yüklenemedi.");
            }
        );
        return () => unsubProducts();
    }, [userData]);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        };
        setLoading(true);
        setError(null);

        const companyId = userData.companyId;
        const startTimestamp = Timestamp.fromDate(new Date(startDate));
        const endTimestamp = Timestamp.fromDate(new Date(endDate + 'T23:59:59'));

        const salesInvoicesQuery = query(
            collection(db, 'companies', companyId, 'invoices'),
            where('invoiceDate', '>=', startTimestamp),
            where('invoiceDate', '<=', endTimestamp)
        );
        const purchaseInvoicesQuery = query(
            collection(db, 'companies', companyId, 'purchaseInvoices'),
            where('invoiceDate', '>=', startTimestamp),
            where('invoiceDate', '<=', endTimestamp)
        );
        const transQuery = query(
            collection(db, 'companies', companyId, 'transactions'),
            where('date', '>=', startTimestamp),
            where('date', '<=', endTimestamp)
        );

        const unsubSalesInvoices = onSnapshot(salesInvoicesQuery, 
            (snapshot) => setSalesInvoices(snapshot.docs.map(d => d.data())),
            (err) => {
                console.error("Satış Faturaları yüklenirken hata:", err);
                setError("Satış Faturası verileri yüklenemedi.");
            }
        );
        
        const unsubPurchaseInvoices = onSnapshot(purchaseInvoicesQuery, 
            (snapshot) => setPurchaseInvoices(snapshot.docs.map(d => d.data())),
            (err) => {
                console.error("Alış Faturaları yüklenirken hata:", err);
                setError("Alış Faturası verileri yüklenemedi.");
            }
        );
        
        const unsubTrans = onSnapshot(transQuery, 
            (snapshot) => {
                setTransactions(snapshot.docs.map(d => d.data()));
                setLoading(false);
            },
            (err) => {
                console.error("İşlemler yüklenirken hata:", err);
                setError("Finansal hareketler yüklenemedi.");
                setLoading(false);
            }
        );

        return () => { unsubSalesInvoices(); unsubPurchaseInvoices(); unsubTrans(); };
    }, [userData, startDate, endDate]);

    const reportData = useMemo(() => {
        const totalSales = salesInvoices.reduce((sum, inv) => sum + (inv.totalTRY || inv.total), 0);
        const totalIncome = transactions.filter(t => t.type === 'Tahsilat').reduce((sum, t) => sum + t.amount, 0);
        const totalExpense = transactions.filter(t => t.type === 'Ödeme').reduce((sum, t) => sum + t.amount, 0);
        const cashFlow = totalIncome - totalExpense;

        const customerSales = salesInvoices.reduce((acc, inv) => {
            acc[inv.customerName] = (acc[inv.customerName] || 0) + (inv.totalTRY || inv.total);
            return acc;
        }, {});

        const topCustomers = Object.entries(customerSales).sort(([, a], [, b]) => b - a).slice(0, 5);
        
        const supplierPurchases = purchaseInvoices.reduce((acc, inv) => {
            acc[inv.supplierName] = (acc[inv.supplierName] || 0) + (inv.totalTRY || inv.total);
            return acc;
        }, {});

        const topSuppliers = Object.entries(supplierPurchases).sort(([, a], [, b]) => b - a).slice(0, 5);

        const productSales = {};
        salesInvoices.forEach(inv => {
            (inv.lineItems || []).forEach(item => {
                if (!productSales[item.productId]) {
                    productSales[item.productId] = { total: 0, quantity: 0 };
                }
                const itemTotal = item.total || (item.price * item.quantity);
                productSales[item.productId].total += (inv.currency === 'USD' ? itemTotal * inv.exchangeRate : itemTotal);
                productSales[item.productId].quantity += item.quantity;
            });
        });

        const topProducts = Object.entries(productSales)
            .map(([productId, data]) => {
                const productInfo = products.find(p => p.id === productId);
                return { name: productInfo ? productInfo.name : 'Bilinmeyen Ürün', ...data };
            })
            .sort((a, b) => b.total - a.total)
            .slice(0, 5);

        return { totalSales, totalIncome, totalExpense, cashFlow, topCustomers, topSuppliers, topProducts };
    }, [salesInvoices, purchaseInvoices, transactions, products]);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Raporlar</h1>
                <div style={styles.dateFilterContainer}>
                    <label>Başlangıç:</label>
                    <input type="date" style={styles.dateInput} value={startDate} onChange={e => setStartDate(e.target.value)} />
                    <label>Bitiş:</label>
                    <input type="date" style={styles.dateInput} value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
            </div>

            {loading && <p>Raporlar yükleniyor...</p>}
            {error && <p style={styles.errorText}>{error}</p>}
            {!loading && !error && (
                <div style={styles.grid}>
                    <div style={styles.card}>
                        <div style={styles.cardTitle}>Toplam Satış (Faturalanmış)</div>
                        <div style={styles.cardValue}>{reportData.totalSales.toFixed(2)} ₺</div>
                    </div>
                    <div style={styles.card}>
                        <div style={styles.cardTitle}>Toplam Tahsilat</div>
                        <div style={{...styles.cardValue, ...styles.income}}>+{reportData.totalIncome.toFixed(2)} ₺</div>
                    </div>
                    <div style={styles.card}>
                        <div style={styles.cardTitle}>Toplam Gider</div>
                        <div style={{...styles.cardValue, ...styles.expense}}>-{reportData.totalExpense.toFixed(2)} ₺</div>
                    </div>
                    <div style={styles.card}>
                        <div style={styles.cardTitle}>Nakit Akışı (Gelir-Gider)</div>
                        <div style={{...styles.cardValue, ...styles.balance}}>{reportData.cashFlow.toFixed(2)} ₺</div>
                    </div>
                    <div style={{...styles.card, gridColumn: 'span 2'}}>
                        <h3 style={styles.cardTitle}>En Çok Ciro Yapan Müşteriler</h3>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Müşteri</th><th style={styles.th}>Toplam Satış</th></tr></thead>
                            <tbody>
                                {reportData.topCustomers.map(([name, total]) => (
                                    <tr key={name}>
                                        <td style={styles.td}>{name}</td>
                                        <td style={styles.td}>{total.toFixed(2)} ₺</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{...styles.card, gridColumn: 'span 2'}}>
                        <h3 style={styles.cardTitle}>En Çok Alım Yapılan Tedarikçiler</h3>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Tedarikçi</th><th style={styles.th}>Toplam Alım</th></tr></thead>
                            <tbody>
                                {reportData.topSuppliers.map(([name, total]) => (
                                    <tr key={name}>
                                        <td style={styles.td}>{name}</td>
                                        <td style={styles.td}>{total.toFixed(2)} ₺</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{...styles.card, gridColumn: 'span 2'}}>
                        <h3 style={styles.cardTitle}>En Çok Satan Ürünler (Ciroya Göre)</h3>
                        <table style={styles.table}>
                            <thead><tr><th style={styles.th}>Ürün</th><th style={styles.th}>Satış Adedi</th><th style={styles.th}>Toplam Ciro</th></tr></thead>
                            <tbody>
                                {reportData.topProducts.map((product) => (
                                    <tr key={product.name}>
                                        <td style={styles.td}>{product.name}</td>
                                        <td style={styles.td}>{product.quantity}</td>
                                        <td style={styles.td}>{product.total.toFixed(2)} ₺</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Reports;
